import { StatsAggregationPeriodDto, StatsIntervalDto } from "services/stats/restModels";
import { endOfDay, startOfDay } from "shared/lib/dateInput";

export type StatsPeriodKey =
    | "today"
    | "yesterday"
    | "week"
    | "previousWeek"
    | "month"
    | "previousMonth"
    | "quarter"
    | "previousQuarter"
    | "year"
    | "previousYear"
    | "custom";

export interface StatsRange {
    from: Date;
    to: Date;
}

export interface StatsPeriodOption {
    key: StatsPeriodKey;
    label: string;
    // Подпись сравнения на плитке: «выручка выросла на 12 % — с предыдущей недели».
    compareLabel: string;
    interval: StatsIntervalDto;
}

const HOURLY: StatsIntervalDto = { amount: 1, period: "Hours" };
const DAILY: StatsIntervalDto = { amount: 1, period: "Days" };
const WEEKLY: StatsIntervalDto = { amount: 1, period: "Weeks" };
const MONTHLY: StatsIntervalDto = { amount: 1, period: "Months" };

/*
* Пресеты периода. Порядок и формулировки — с прежнего экрана; шаг детализации по умолчанию
* подобран под длину периода (у старой страницы «сегодня» рисовалось одной точкой на сутки, а
* квартал — девяноста).
* */
export const STATS_PERIOD_OPTIONS: StatsPeriodOption[] = [
    { key: "today", label: "Сегодня", compareLabel: "Со вчерашнего дня", interval: HOURLY },
    { key: "yesterday", label: "Вчера", compareLabel: "С позавчерашнего дня", interval: HOURLY },
    { key: "week", label: "С начала недели", compareLabel: "С предыдущей недели", interval: DAILY },
    {
        key: "previousWeek",
        label: "Предыдущая неделя",
        compareLabel: "С предшествующей недели",
        interval: DAILY,
    },
    { key: "month", label: "С начала месяца", compareLabel: "С предыдущего месяца", interval: DAILY },
    {
        key: "previousMonth",
        label: "Предыдущий месяц",
        compareLabel: "С предшествующего месяца",
        interval: DAILY,
    },
    {
        key: "quarter",
        label: "С начала квартала",
        compareLabel: "С предыдущего квартала",
        interval: WEEKLY,
    },
    {
        key: "previousQuarter",
        label: "Предыдущий квартал",
        compareLabel: "С предшествующего квартала",
        interval: WEEKLY,
    },
    { key: "year", label: "С начала года", compareLabel: "С предыдущего года", interval: MONTHLY },
    {
        key: "previousYear",
        label: "Предыдущий год",
        compareLabel: "С предшествующего года",
        interval: MONTHLY,
    },
];

export const CUSTOM_PERIOD_OPTION: StatsPeriodOption = {
    key: "custom",
    label: "Произвольный период",
    compareLabel: "С предыдущего периода",
    interval: DAILY,
};

// Шаг «минуты» бэк принимает, но в кабинете его нет: сутки поминутно — это 1440 точек,
// больше лимита в тысячу границ и всё равно нечитаемо.
export const STATS_INTERVAL_PERIOD_OPTIONS: {
    value: StatsAggregationPeriodDto;
    label: string;
}[] = [
    { value: "Hours", label: "часы" },
    { value: "Days", label: "дни" },
    { value: "Weeks", label: "недели" },
    { value: "Months", label: "месяцы" },
];

// Столько границ окон принимает бэк (StatsBucketsUtils.MAX_BOUNDARIES) — больше он отвечает отказом.
export const MAX_CHART_POINTS = 1000;

export const findStatsPeriodOption = (key: StatsPeriodKey): StatsPeriodOption =>
    STATS_PERIOD_OPTIONS.find((option) => option.key === key) || CUSTOM_PERIOD_OPTION;

const MILLIS_IN_DAY = 24 * 60 * 60 * 1000;

const addDays = (date: Date, days: number): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const addMonths = (date: Date, months: number): Date =>
    new Date(date.getFullYear(), date.getMonth() + months, date.getDate());

// Неделя начинается с понедельника: прежний экран добивался этого патчем локали moment.
const startOfWeek = (date: Date): Date => {
    const day = startOfDay(date);

    return addDays(day, -((day.getDay() + 6) % 7));
};

const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

const startOfQuarter = (date: Date): Date =>
    new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);

const startOfYear = (date: Date): Date => new Date(date.getFullYear(), 0, 1);

/*
* Конец завершившегося периода — за миллисекунду до начала следующего. Иначе заказ, созданный
* ровно в полночь понедельника, попадает и в прошлую неделю, и в эту.
* */
const endBefore = (date: Date): Date => new Date(date.getTime() - 1);

/*
* У незавершённых периодов («сегодня», «с начала недели») правая граница — текущий момент, а не
* конец суток. Иначе ломаются сразу две вещи: сравнение делит прошедшую часть дня на целые
* сутки прошлого периода (в десять утра «Выручка» показывала бы −55 % каждый день), а график с
* часовым шагом дорисовывал бы нулевую линию от текущего часа до полуночи.
* */
export const buildStatsRange = (key: StatsPeriodKey, now: Date): StatsRange => {
    switch (key) {
        case "yesterday": {
            const yesterday = addDays(now, -1);

            return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
        }
        case "week":
            return { from: startOfWeek(now), to: now };
        case "previousWeek": {
            const start = startOfWeek(now);

            return { from: addDays(start, -7), to: endBefore(start) };
        }
        case "month":
            return { from: startOfMonth(now), to: now };
        case "previousMonth": {
            const start = startOfMonth(now);

            return { from: addMonths(start, -1), to: endBefore(start) };
        }
        case "quarter":
            return { from: startOfQuarter(now), to: now };
        case "previousQuarter": {
            const start = startOfQuarter(now);

            return { from: addMonths(start, -3), to: endBefore(start) };
        }
        case "year":
            return { from: startOfYear(now), to: now };
        case "previousYear": {
            const start = startOfYear(now);

            return { from: addMonths(start, -12), to: endBefore(start) };
        }
        default:
            return { from: startOfDay(now), to: now };
    }
};

/*
* Окно сравнения. У завершившихся периодов это предыдущий такой же, а у незаконченных
* («с начала недели») — столько же времени от начала прошлой недели: сравнивать среду с
* пятницей и субботой бессмысленно, поэтому сдвигом на длину периода тут не обойтись.
* */
export const buildStatsCompareRange = (key: StatsPeriodKey, range: StatsRange): StatsRange => {
    const elapsed = range.to.getTime() - range.from.getTime();

    const sameLengthFrom = (start: Date): StatsRange => ({
        from: start,
        to: new Date(start.getTime() + elapsed),
    });

    switch (key) {
        // Сегодняшние десять часов сравниваем с первыми десятью часами вчерашнего дня, а не с
        // целыми вчерашними сутками.
        case "today":
            return sameLengthFrom(addDays(range.from, -1));
        case "yesterday":
            // range.from — уже начало вчерашнего дня, и buildStatsRange отнимет от него сутки сам.
            return buildStatsRange("yesterday", range.from);
        case "week":
            return sameLengthFrom(addDays(range.from, -7));
        case "previousWeek":
            return { from: addDays(range.from, -7), to: endBefore(range.from) };
        case "month":
            return sameLengthFrom(addMonths(range.from, -1));
        case "previousMonth":
            return { from: addMonths(range.from, -1), to: endBefore(range.from) };
        case "quarter":
            return sameLengthFrom(addMonths(range.from, -3));
        case "previousQuarter":
            return { from: addMonths(range.from, -3), to: endBefore(range.from) };
        case "year":
            return sameLengthFrom(addMonths(range.from, -12));
        case "previousYear":
            return { from: addMonths(range.from, -12), to: endBefore(range.from) };
        default:
            // Произвольный период сравниваем с таким же, вплотную предшествующим ему.
            return { from: new Date(range.from.getTime() - elapsed), to: endBefore(range.from) };
    }
};

const INTERVAL_MILLIS: { [key in StatsAggregationPeriodDto]: number } = {
    Minutes: 60 * 1000,
    Hours: 60 * 60 * 1000,
    Days: MILLIS_IN_DAY,
    Weeks: 7 * MILLIS_IN_DAY,
    Months: 30 * MILLIS_IN_DAY,
};

/*
* Оценка числа точек, а не точный счёт: месяц берём за тридцать дней. Нужна только чтобы
* предупредить о заведомо неподъёмной комбинации до запроса — иначе бэк отвечает отказом, а
* текст причины до пользователя не доходит (restGet отдаёт при неуспехе просто null).
* */
export const estimateChartPoints = (range: StatsRange, interval: StatsIntervalDto): number =>
    Math.ceil(
        (range.to.getTime() - range.from.getTime()) /
            (INTERVAL_MILLIS[interval.period] * interval.amount)
    );

/*
* Шаг детализации для произвольного периода: у пресета он свой, а здесь единственный ориентир —
* длина. Границы подобраны так, чтобы на графике оставалось от десятка до сотни точек.
* */
export const defaultIntervalForRange = (range: StatsRange): StatsIntervalDto => {
    const days = (range.to.getTime() - range.from.getTime()) / MILLIS_IN_DAY;

    if (days <= 2) return HOURLY;
    if (days <= 60) return DAILY;
    if (days <= 400) return WEEKLY;

    return MONTHLY;
};
